import { QueryClient, QueryKey } from '@tanstack/react-query'
import {
  type PersistedClient,
  type Persister,
} from '@tanstack/react-query-persist-client'
import { get, set, del } from 'idb-keyval'
import { type Note } from '../../utils/note'
import { type AuthenticatedUser } from '../../utils/User'
import { cacheKeys } from '../../utils/queryKeyFactory'

// Define a type for the cache data
export type CacheData = {
  map: Map<string, Note>
  version: number
}

/**
 * Creates an Indexed DB persister
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 * @see https://tanstack.com/query/v4/docs/react/plugins/persistQueryClient#building-a-persister
 */
export function createIDBPersister(idbValidKey: IDBValidKey = 'notes') {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(idbValidKey, client)
    },
    restoreClient: async () => {
      return await get<PersistedClient>(idbValidKey)
    },
    removeClient: async () => {
      await del(idbValidKey)
    },
  } as Persister
}

export const updateCache = (
  queryClient: QueryClient,
  changedNotes: Note[],
  queryKey: QueryKey
) => {
  queryClient.setQueryData<CacheData>(queryKey, (oldData) => {
    if (!oldData) return { map: new Map(), version: 0 }

    // Create a new Map to ensure React detects the change
    const newMap = new Map(oldData.map)

    for (const note of changedNotes) {
      if (note.recordName) {
        newMap.set(note.recordName, note)
      }
    }

    return { map: newMap, version: oldData.version + 1 }
  })
}

export const removeFromCacheByRecordNames = (
  queryClient: QueryClient,
  recordNames: string[],
  queryKey: QueryKey
) => {
  queryClient.setQueryData<CacheData>(queryKey, (oldData) => {
    if (!oldData) return { map: new Map(), version: 0 }

    // Create a new Map to ensure React detects the change
    const newMap = new Map(oldData.map)

    for (const recordName of recordNames) {
      newMap.delete(recordName)
    }

    return { map: newMap, version: oldData.version + 1 }
  })
}

export const removeFromCache = (
  queryClient: QueryClient,
  changedNotes: Note[],
  queryKey: QueryKey
) => {
  const recordNames = changedNotes
    .map((note) => note.recordName)
    .filter((name): name is string => !!name)
  removeFromCacheByRecordNames(queryClient, recordNames, queryKey)
}

export const getCurrentCacheVersion = (
  queryClient: QueryClient,
  queryKey: QueryKey
) => {
  const data = queryClient.getQueryData<CacheData>(queryKey)
  if (!data) return 0
  return data.version
}

export const getCachedData = (queryClient: QueryClient, queryKey: QueryKey) => {
  const data = queryClient.getQueryData<CacheData>(queryKey)
  if (!data) return new Map<string, Note>()
  return data.map
}

/**
 * Get a note from the private project notes cache
 * @param queryClient The React Query client
 * @param userId The user ID
 * @param recordName The record name to look up
 * @returns The note if found, undefined otherwise
 */
export function getPrivateProjectNote(
  queryClient: QueryClient,
  userId: string,
  recordName: string
): Note | undefined {
  const privateProjectKey = cacheKeys.privateProjectNotes(userId)
  const privateData = queryClient.getQueryData<CacheData>(privateProjectKey)
  return privateData?.map.get(recordName)
}

/**
 * Get a note from the team project notes cache
 * @param queryClient The React Query client
 * @param userId The user ID
 * @param recordName The record name to look up
 * @returns The note if found, undefined otherwise
 */
export function getTeamProjectNote(
  queryClient: QueryClient,
  userId: string,
  recordName: string
): Note | undefined {
  const teamProjectKey = cacheKeys.teamProjectNotes(userId)
  const teamData = queryClient.getQueryData<CacheData>(teamProjectKey)
  return teamData?.map.get(recordName)
}

/**
 * Get a note from the private calendar notes cache
 * @param queryClient The React Query client
 * @param userId The user ID
 * @param recordName The record name to look up
 * @returns The note if found, undefined otherwise
 */
export function getPrivateCalendarNote(
  queryClient: QueryClient,
  userId: string,
  recordName: string
): Note | undefined {
  const privateCalendarKey = cacheKeys.privateCalendarNotes(userId)
  const privateData = queryClient.getQueryData<CacheData>(privateCalendarKey)
  return privateData?.map.get(recordName)
}

/**
 * Get a note from the team calendar notes cache
 * @param queryClient The React Query client
 * @param userId The user ID
 * @param recordName The record name to look up
 * @returns The note if found, undefined otherwise
 */
export function getTeamCalendarNote(
  queryClient: QueryClient,
  userId: string,
  recordName: string
): Note | undefined {
  const teamCalendarKey = cacheKeys.teamCalendarNotes(userId)
  const teamData = queryClient.getQueryData<CacheData>(teamCalendarKey)
  return teamData?.map.get(recordName)
}

/**
 * Get a note from any cache (private or team, project or calendar)
 * @param queryClient The React Query client
 * @param userId The user ID
 * @param recordName The record name to look up
 * @returns The note if found, undefined otherwise
 */
export function getNoteFromAnyCache(
  queryClient: QueryClient,
  userId: string,
  recordName: string
): Note | undefined {
  return (
    getPrivateProjectNote(queryClient, userId, recordName) ??
    getTeamProjectNote(queryClient, userId, recordName) ??
    getPrivateCalendarNote(queryClient, userId, recordName) ??
    getTeamCalendarNote(queryClient, userId, recordName)
  )
}

/**
 * Get a note from the appropriate cache based on the user and note type
 * @param queryClient The React Query client
 * @param queryKey The cache query key
 * @param recordName The record name to look up
 * @returns The note if found, undefined otherwise
 */
export function getNoteFromCache(
  queryClient: QueryClient,
  queryKey: QueryKey,
  recordName: string
): Note | undefined {
  const data = queryClient.getQueryData<CacheData>(queryKey)
  return data?.map.get(recordName)
}

/**
 * Get a note from either private or team project notes cache
 * @param queryClient The React Query client
 * @param user The authenticated user
 * @param recordName The record name to look up
 * @returns The note if found, undefined otherwise
 */
export function getProjectNote(
  queryClient: QueryClient,
  user: AuthenticatedUser,
  recordName: string
): Note | undefined {
  return (
    getPrivateProjectNote(queryClient, user.userId, recordName) ??
    getTeamProjectNote(queryClient, user.userId, recordName)
  )
}
